/**
 * @copyright Copyright 2022-2025 Epic Systems Corporation
 * @file Drop down filter control component
 * @module Epic.AppOrchard.Core.Filter.FilterControl
 */

import {
	Box,
	BoxProps,
	Button,
	Center,
	Checkbox,
	Flex,
	Icon,
	IconButton,
	Input,
	Spacer,
	Switch,
	Text,
	useToast,
	useToken,
} from "@chakra-ui/react";
import { useAsync } from "@epic/react-async-hook";
import { config } from "ao/appConfig";
import { ProgressiveLoadComboBox } from "ao/components/ConnectorManagement/frameworkComponents/Shared/ProgressiveLoadComboBox";
import { IFilterDefinition, IFilterListItem, SelectOptionType } from "ao/types";
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaAngleDoubleDown, FaAngleDoubleUp } from "react-icons/fa";
import { RiFilterFill, RiFilterOffLine } from "react-icons/ri";
import StateManagedSelect, { GroupBase, MultiValue, SingleValue, StylesConfig } from "react-select";
import AsyncSelect from "react-select/async";
import Select from "react-select/dist/declarations/src/Select";

export interface IFilterItemProps {
	filterDef: IFilterDefinition;
	filterListItem: IFilterListItem;
	handleUpdateFilter: (
		filter: IFilterListItem,
		setColsWithFilter?: boolean,
		filterColumnMappings?: Record<string, string>,
	) => void;
	handleClearFilter: (filterKey: string, isToggled?: boolean) => void;
	setColsWithFilter?: boolean;
	filterToColumnMapping?: Record<string, string>;
}

export const FilterControl: FC<IFilterItemProps & BoxProps> = memo((props: IFilterItemProps & BoxProps) => {
	const {
		filterDef,
		filterListItem,
		handleUpdateFilter,
		handleClearFilter,
		setColsWithFilter,
		filterToColumnMapping,
		...rest
	} = props;
	const filterMaxSelection = 10;
	const shouldAllowClear = filterDef.allowClear !== undefined ? filterDef.allowClear : true;
	const shouldAllowCollapse = filterDef.allowCollapse ?? true;
	const toast = useToast();

	//#region state/hooks/handlers
	const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(filterDef.expandedByDefault ?? false);
	const [search, setSearch] = useState<string>("");
	const [hasAsyncLoadRunOnce, setHasAsyncLoadRunOnce] = useState<boolean>(false);
	const [asyncDefaultOptions, setAsyncDefaultOptions] = useState<SelectOptionType[]>([]);
	const asyncSelectRef = useRef<Select<SelectOptionType, boolean, GroupBase<SelectOptionType>>>(null);

	const FilterSelect = filterDef.useProgressiveComboBox ? ProgressiveLoadComboBox : StateManagedSelect;

	const toggleSwitch = () => {
		handleChangeToggle();
	};

	const handleChangeExclude = () => {
		handleUpdateFilter({ ...filterListItem, isExcluded: !filterListItem.isExcluded });
	};

	const handleChangeToggle = () => {
		handleUpdateFilter({ ...filterListItem, isToggled: !filterListItem.isToggled });
	};

	const handleChangeValue = (option: SingleValue<SelectOptionType>) => {
		if (option)
			handleUpdateFilter(
				{ ...filterListItem, value: option.value },
				setColsWithFilter,
				filterToColumnMapping,
			);
	};

	const handleChangeValues = (options: MultiValue<SelectOptionType>) => {
		if (options)
			handleUpdateFilter(
				{ ...filterListItem, values: options.map((o) => parseInt(o.value)) },
				setColsWithFilter,
				filterToColumnMapping,
			);
	};

	const handleFilterClear = () => {
		handleClearFilter(filterListItem.key, filterDef?.customToggle?.enabledByDefault);
		if (asyncSelectRef && asyncSelectRef.current) asyncSelectRef.current.clearValue();
	};

	const filterHasValue =
		!!filterListItem.value || (!!filterListItem.values && !!filterListItem.values.length);

	const filterIsExcluded = filterHasValue && filterListItem.isExcluded;
	const filterCanClear =
		filterHasValue ||
		filterListItem.isExcluded ||
		(filterListItem?.isToggled ?? false) !== (filterDef?.customToggle?.enabledByDefault ?? false);

	const errorRed = useToken("colors", "red.500");

	const colorStyles: StylesConfig<SelectOptionType, false, GroupBase<SelectOptionType>> = useMemo(() => {
		return {
			control: (base, state) => ({
				...base,
				...(filterDef.isRequired &&
					!filterHasValue &&
					!state.isFocused && {
						"border-color": errorRed,
						"box-shadow": `${errorRed} 0px 0px 0px 1px`,
					}),
			}),
		};
	}, [errorRed, filterDef.isRequired, filterHasValue]);

	const lookupOptions = useMemo(() => {
		const options = filterDef.options && Array.isArray(filterDef.options) ? filterDef.options : [];
		const mappedOptions = options.map<SelectOptionType>((record) => {
			const label = filterDef.includeIdInOption ? `${record.name} [${record.id}]` : record.name;
			return { label, value: record.id.toString() };
		});
		return mappedOptions;
	}, [filterDef.includeIdInOption, filterDef.options]);

	const doNotRequireMinSearchLength = filterDef.minSearchLength === undefined;
	const minSearchLength = filterDef.minSearchLength || 0;
	const minSearchLengthMet = search && search.length >= minSearchLength;

	const filterOptions: readonly SelectOptionType[] =
		doNotRequireMinSearchLength || minSearchLengthMet ? lookupOptions : [];

	const emptyPromise = () =>
		new Promise<SelectOptionType[]>(() => {
			return;
		});

	const [, handleSearchData] = useAsync(filterDef.asyncLoad ?? emptyPromise, {
		executeImmediately: false,
		displayName: "quickSearch",
		onSuccess: (response, extras) => {
			const [, , , , callback] = extras.params;

			if (callback) {
				callback(response);
			}
		},
		onFailure: () => {
			toast({
				title: "Search Error",
				description: "We were unable to search. Try again.",
				status: "error",
				duration: config.ToastDuration,
				isClosable: true,
			});
		},
	});

	const filterValuesAsNumbers = useMemo(() => {
		return (
			(filterDef.isMultiResponse ? filterListItem.values : [filterListItem.value])
				?.map((value: string | number | undefined) => {
					if (value === undefined) {
						return -1;
					}
					if (typeof value === "number") {
						return value;
					}

					const valueAsInt = parseInt(value);
					if (isNaN(valueAsInt)) {
						return -1;
					}
					return valueAsInt;
				})
				?.filter((value) => value >= 0) || []
		);
	}, [filterDef.isMultiResponse, filterListItem.value, filterListItem.values]);

	const loadOptions = useCallback(
		(inputValue: string, callback: (options: SelectOptionType[]) => void) => {
			if (!filterDef.asyncLoad) {
				return;
			}

			const inputQualifiesForSearch =
				doNotRequireMinSearchLength || (inputValue && inputValue.length >= minSearchLength);

			if (inputQualifiesForSearch) {
				handleSearchData(inputValue, [], filterValuesAsNumbers, 10, callback);
			}
		},
		[
			doNotRequireMinSearchLength,
			filterDef.asyncLoad,
			filterValuesAsNumbers,
			handleSearchData,
			minSearchLength,
		],
	);

	useEffect(() => {
		if (filterDef && filterDef.type === "asyncSelect" && !hasAsyncLoadRunOnce) {
			setHasAsyncLoadRunOnce(true);

			if (filterValuesAsNumbers.length > 0) {
				handleSearchData(
					"",
					filterValuesAsNumbers,
					[],
					filterValuesAsNumbers.length,
					setAsyncDefaultOptions,
				);
			}
		}
	}, [filterDef, filterValuesAsNumbers, handleSearchData, hasAsyncLoadRunOnce]);

	//#endregion
	return (
		<Box w="100%" borderWidth="1px" borderRadius="3px" {...rest} key={filterDef.key}>
			<Box
				as={shouldAllowCollapse ? "button" : undefined}
				width="100%"
				padding={2}
				{...(shouldAllowCollapse && {
					onClick: () => setIsFilterExpanded(!isFilterExpanded),
					_hover: { bg: "gray.100" },
				})}
			>
				<Flex align={"center"}>
					<Text title={filterDef.tooltip} fontWeight={filterHasValue ? "bold" : "normal"}>
						{filterDef.name}
						{filterDef.isRequired && (
							<Text marginLeft={1} as="span" color={errorRed}>
								*
							</Text>
						)}
					</Text>
					<Spacer />
					{filterIsExcluded ? (
						<Center>
							<Icon
								verticalAlign=""
								aria-label="Filter Excluded"
								title="Filter Excluded"
								as={RiFilterOffLine}
								color={"black"}
							></Icon>
						</Center>
					) : (
						filterHasValue && (
							<Center>
								<Icon
									verticalAlign=""
									aria-label="Filter Applied"
									title="Filter Applied"
									as={RiFilterFill}
								></Icon>
							</Center>
						)
					)}
					{shouldAllowCollapse && (
						<IconButton
							size="xs"
							aria-label="Expand/Collapse Filter"
							title="Expand/Collapse Filter"
							icon={isFilterExpanded ? <FaAngleDoubleUp /> : <FaAngleDoubleDown />}
							onClick={() => setIsFilterExpanded(!isFilterExpanded)}
							variant="ghost"
							tabIndex={-1}
						></IconButton>
					)}
				</Flex>
			</Box>
			{isFilterExpanded && (
				<Box m={2}>
					<Box mb="3px">
						{filterDef.customToggle && (
							<Flex mb={3}>
								<Switch
									isChecked={filterListItem.isToggled}
									size="sm"
									ml="2"
									isDisabled={!filterHasValue}
									onChange={() => {
										toggleSwitch();
									}}
								/>
								<Text
									title={filterDef.customToggle.tooltip}
									fontSize="sm"
									ml="2"
									color="black"
									cursor="pointer"
									onClick={() => toggleSwitch()}
								>
									{filterDef.customToggle.name}
								</Text>
							</Flex>
						)}
						{(filterDef.type === undefined || filterDef.type === "select") && (
							<>
								{filterDef.isMultiResponse ? (
									<FilterSelect
										isMulti
										value={lookupOptions
											.filter(
												(o) =>
													(filterListItem.values?.filter(
														(filterValue) => filterValue.toString() === o.value,
													)?.length || 0) > 0,
											)
											.map<SelectOptionType>(
												(r): SelectOptionType => {
													return { label: r.label, value: r.value };
												},
											)}
										options={filterOptions}
										onChange={handleChangeValues}
										isClearable={false}
										isOptionDisabled={() => {
											return (
												lookupOptions.filter(
													(o) =>
														(filterListItem.values?.filter(
															(filterValue) =>
																filterValue.toString() === o.value,
														)?.length || 0) > 0,
												).length > filterMaxSelection
											);
										}}
										onInputChange={(newValue: string) => {
											setSearch(newValue);
										}}
										noOptionsMessage={() =>
											doNotRequireMinSearchLength || minSearchLengthMet
												? "No options"
												: `Enter at least ${minSearchLength} characters to search`
										}
									/>
								) : (
									<FilterSelect
										value={lookupOptions
											.filter((o) => filterListItem.value === o.value)
											.map<SelectOptionType>(
												(r): SelectOptionType => {
													return { label: r.label, value: r.value };
												},
											)}
										options={filterOptions}
										onChange={handleChangeValue}
										isClearable={false}
										onInputChange={(newValue: string) => {
											setSearch(newValue);
										}}
										noOptionsMessage={() =>
											doNotRequireMinSearchLength || minSearchLengthMet
												? "No options"
												: `Enter at least ${minSearchLength} characters to search`
										}
										styles={colorStyles}
									/>
								)}
							</>
						)}
						{filterDef.type === "asyncSelect" && filterDef.asyncLoad && (
							<AsyncSelect
								ref={asyncSelectRef}
								isMulti={filterDef.isMultiResponse}
								cacheOptions
								defaultValue={asyncDefaultOptions}
								loadOptions={loadOptions}
								onChange={(newValue) => {
									if (filterDef.isMultiResponse) {
										handleChangeValues(newValue as MultiValue<SelectOptionType>);
									} else {
										handleChangeValue(newValue as SingleValue<SelectOptionType>);
									}
								}}
								isClearable={true}
								loadingMessage={() =>
									doNotRequireMinSearchLength || minSearchLengthMet
										? undefined
										: `Enter at least ${minSearchLength} characters to search`
								}
								noOptionsMessage={() =>
									doNotRequireMinSearchLength || minSearchLengthMet
										? "No options"
										: `Enter at least ${minSearchLength} characters to search`
								}
							/>
						)}
						{filterDef.type === "input" && (
							<>
								{filterDef.inputType === "date" || filterDef.inputType === "number" ? (
									<Input
										type={filterDef.inputType}
										value={filterListItem.value || ""}
										min={filterDef.minValue || ""}
										max={filterDef.maxValue || ""}
										onChange={(ev) =>
											handleUpdateFilter({ ...filterListItem, value: ev.target.value })
										}
									/>
								) : (
									<Input
										type={filterDef.inputType || "text"}
										value={filterListItem.value || ""}
										onChange={(ev) =>
											handleUpdateFilter({ ...filterListItem, value: ev.target.value })
										}
									/>
								)}
							</>
						)}
					</Box>
					<Flex>
						{shouldAllowClear && (
							<>
								<Button
									size="xs"
									variant="outline"
									onClick={() => handleFilterClear()}
									isDisabled={!filterCanClear}
								>
									Clear
								</Button>
								<Spacer></Spacer>
							</>
						)}
						{filterDef.allowExclude && (
							<Checkbox
								size="sm"
								isChecked={filterListItem.isExcluded}
								isDisabled={!filterHasValue}
								onChange={() => {
									handleChangeExclude();
								}}
							>
								Exclude
							</Checkbox>
						)}
					</Flex>
				</Box>
			)}
		</Box>
	);
});
